import MuiMenu from '@mui/material/Menu';
import type { ReactNode } from 'react';

interface Origin {
  vertical: 'top' | 'center' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

// MUI auto-focuses only the first or the selected option; the list is in the DOM once entering starts.
const focusLastOption = (paper: HTMLElement): void => {
  const options = paper.querySelectorAll<HTMLElement>('[role^="menuitem"]');
  options[options.length - 1]?.focus();
};

/** Caller contract: `aria-controls={open ? id : undefined}` on the trigger. */
export interface MenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  anchorOrigin?: Origin;
  transformOrigin?: Origin;
  // Lands on the role="menu" list. The trigger references it only while open (the list unmounts on
  // close); generate it with useId().
  id?: string;
  // Names the menu when no visible heading does.
  'aria-label'?: string;
  // Which option takes focus on open. Unset keeps MUI's default: the selected option.
  initialFocus?: 'first' | 'last';
  children?: ReactNode;
}

export function Menu({
  anchorEl,
  open,
  onClose,
  anchorOrigin,
  transformOrigin,
  id,
  initialFocus,
  children,
  ...aria
}: MenuProps) {
  const ariaLabel = aria['aria-label'];
  // On the list, not the Popover root: MUI 6.4 has no slotProps.list.
  const listProps = {
    ...(id !== undefined ? { id } : {}),
    ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
  };
  return (
    <MuiMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      {...(anchorOrigin !== undefined ? { anchorOrigin } : {})}
      {...(transformOrigin !== undefined ? { transformOrigin } : {})}
      {...(Object.keys(listProps).length > 0 ? { MenuListProps: listProps } : {})}
      // `menu` ignores the selected option, so focus lands where the trigger asked.
      {...(initialFocus !== undefined ? { variant: 'menu' as const } : {})}
      {...(initialFocus === 'last' ? { TransitionProps: { onEntering: focusLastOption } } : {})}
    >
      {children}
    </MuiMenu>
  );
}
